<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const store = useStore();
const { t } = useI18n();

const years = useMapGetter('disparos/getYears');
const contatos = useMapGetter('disparos/getContatos');
const uiFlags = useMapGetter('disparos/getUIFlags');

const selectedYear = ref('');
const selectedCodDivPars = ref(new Set());
const search = ref('');
const selectedTemplate = ref('notificacao_divida');

const templates = computed(() => [
  {
    value: 'notificacao_divida',
    label: t('DISPARO.DIVIDAS.TEMPLATES.NOTIFICACAO_DIVIDA'),
  },
  {
    value: 'refis_tributario',
    label: t('DISPARO.DIVIDAS.TEMPLATES.REFIS_TRIBUTARIO'),
  },
  {
    value: 'regras_parcelamento_tributario',
    label: t('DISPARO.DIVIDAS.TEMPLATES.REGRAS_PARCELAMENTO_TRIBUTARIO'),
  },
]);

onMounted(() => {
  store.dispatch('disparos/fetchYears');
});

let searchTimer = null;

const reloadContatos = () => {
  const year = selectedYear.value;
  const term = search.value.trim();
  if (year) {
    store.dispatch('disparos/fetchByYear', year);
  } else if (term) {
    store.dispatch('disparos/searchContatos', term);
  } else {
    store.dispatch('disparos/clearContatos');
  }
};

watch(selectedYear, () => {
  if (searchTimer) clearTimeout(searchTimer);
  reloadContatos();
});

watch(search, () => {
  if (selectedYear.value) return;
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(reloadContatos, 300);
});

const clearSelection = () => {
  selectedCodDivPars.value = new Set();
};

const filteredContatos = computed(() => {
  const term = search.value.trim().toLowerCase();
  if (!term) return contatos.value;
  return contatos.value.filter(
    c =>
      (c.nome_contribuinte || '').toLowerCase().includes(term) ||
      (c.celular || '').toLowerCase().includes(term) ||
      (c.descricao_rep || '').toLowerCase().includes(term)
  );
});

// const allFilteredSelected = computed(
//   () =>
//     filteredContatos.value.length > 0 &&
//     filteredContatos.value.every(c => selectedIds.value.has(c.id))
// );

const isSelected = codDivPar => selectedCodDivPars.value.has(codDivPar);

const toggle = codDivPar => {
  if (selectedCodDivPars.value.has(codDivPar))
    selectedCodDivPars.value.delete(codDivPar);
  else selectedCodDivPars.value.add(codDivPar);
  selectedCodDivPars.value = new Set(selectedCodDivPars.value);
};

// const toggleAllFiltered = () => {
//   const next = new Set(selectedIds.value);
//   if (allFilteredSelected.value) {
//     filteredContatos.value.forEach(c => next.delete(c.id));
//   } else {
//     filteredContatos.value.forEach(c => next.add(c.id));
//   }
//   selectedIds.value = next;
// };

const dispatch = async () => {
  const codDivPars = Array.from(selectedCodDivPars.value);
  if (!codDivPars.length) {
    useAlert(t('DISPARO.DIVIDAS.NO_SELECTION'));
    return;
  }
  if (
    !window.confirm(t('DISPARO.DIVIDAS.CONFIRM', { count: codDivPars.length }))
  )
    return;

  const result = await store.dispatch('disparos/dispatchSelected', {
    codDivPars,
    template: selectedTemplate.value,
  });
  if (result?.success) {
    useAlert(t('DISPARO.DIVIDAS.DISPATCH_OK', { count: result.total }));
    selectedCodDivPars.value = new Set();
  } else if (result?.error) {
    useAlert(result.error);
  }
};
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden p-6 gap-4">
    <header class="flex flex-col gap-1">
      <h1 class="text-2xl font-semibold text-n-slate-12">
        {{ t('DISPARO.DIVIDAS.TITLE') }}
      </h1>
      <p class="text-sm text-n-slate-11">
        {{ t('DISPARO.DIVIDAS.DESCRIPTION') }}
      </p>
    </header>

    <div
      class="grid grid-cols-[260px_180px_1fr_auto] gap-x-3 gap-y-1 items-center"
    >
      <label class="row-start-1 text-sm font-medium text-n-slate-12">
        {{ t('DISPARO.DIVIDAS.TEMPLATE_LABEL') }}
      </label>
      <label class="row-start-1 text-sm font-medium text-n-slate-12">
        {{ t('DISPARO.DIVIDAS.YEAR_LABEL') }}
      </label>
      <label class="row-start-1 text-sm font-medium text-n-slate-12">
        {{ t('DISPARO.DIVIDAS.SEARCH_LABEL') }}
      </label>
      <span class="row-start-1" />

      <select
        v-model="selectedTemplate"
        class="row-start-2 h-10 border border-n-slate-4 rounded-md px-3 bg-n-alpha-2 text-n-slate-12 text-sm"
      >
        <option v-for="tpl in templates" :key="tpl.value" :value="tpl.value">
          {{ tpl.label }}
        </option>
      </select>
      <select
        v-model="selectedYear"
        class="row-start-2 h-10 border border-n-slate-4 rounded-md px-3 bg-n-alpha-2 text-n-slate-12 text-sm"
        :disabled="uiFlags.isFetchingYears"
      >
        <option value="">{{ t('DISPARO.DIVIDAS.YEAR_PLACEHOLDER') }}</option>
        <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
      </select>
      <Input
        v-model="search"
        class="row-start-2"
        :placeholder="t('DISPARO.DIVIDAS.SEARCH_PLACEHOLDER')"
      />
      <div class="row-start-2 flex items-center">
        <Button
          :label="
            t('DISPARO.DIVIDAS.DISPATCH', { count: selectedCodDivPars.size })
          "
          :is-loading="uiFlags.isDispatching"
          :disabled="uiFlags.isDispatching || !selectedCodDivPars.size"
          @click="dispatch"
        />
      </div>
    </div>

    <div
      v-if="selectedCodDivPars.size"
      class="flex items-center gap-3 text-sm text-n-slate-11"
    >
      <span>{{
        t('DISPARO.DIVIDAS.SELECTED_COUNT', { count: selectedCodDivPars.size })
      }}</span>
      <button
        type="button"
        class="text-xs underline hover:text-n-slate-12"
        @click="clearSelection"
      >
        {{ t('DISPARO.DIVIDAS.CLEAR_SELECTION') }}
      </button>
    </div>

    <div class="flex-1 overflow-auto border border-n-slate-4 rounded-md">
      <table class="w-full text-sm">
        <thead class="bg-n-alpha-2 sticky top-0">
          <tr class="text-left text-n-slate-11">
            <th class="px-3 py-2 w-10">
              <!--
              <input
                type="checkbox"
                :checked="allFilteredSelected"
                :disabled="!filteredContatos.length"
                @change="toggleAllFiltered"
              />
              -->
            </th>
            <th class="px-3 py-2">{{ t('DISPARO.DIVIDAS.COL.NAME') }}</th>
            <th class="px-3 py-2">{{ t('DISPARO.DIVIDAS.COL.PHONE') }}</th>
            <th class="px-3 py-2">{{ t('DISPARO.DIVIDAS.COL.TRIBUTE') }}</th>
            <th class="px-3 py-2">{{ t('DISPARO.DIVIDAS.COL.YEAR') }}</th>
            <th class="px-3 py-2">
              {{ t('DISPARO.DIVIDAS.COL.COD_DIV_PAR') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-if="uiFlags.isFetchingContatos"
            class="text-center text-n-slate-11"
          >
            <td colspan="6" class="px-3 py-6">
              {{ t('DISPARO.DIVIDAS.LOADING') }}
            </td>
          </tr>
          <tr
            v-else-if="!selectedYear && !search.trim()"
            class="text-center text-n-slate-11"
          >
            <td colspan="6" class="px-3 py-6">
              {{ t('DISPARO.DIVIDAS.SELECT_YEAR') }}
            </td>
          </tr>
          <tr
            v-else-if="!filteredContatos.length"
            class="text-center text-n-slate-11"
          >
            <td colspan="6" class="px-3 py-6">
              {{ t('DISPARO.DIVIDAS.EMPTY') }}
            </td>
          </tr>
          <template v-else>
            <tr
              v-for="row in filteredContatos"
              :key="row.cod_div_par"
              class="border-t border-n-slate-4 hover:bg-n-alpha-2"
            >
              <td class="px-3 py-2">
                <input
                  type="checkbox"
                  class="w-5 h-5 cursor-pointer accent-n-brand"
                  :checked="isSelected(row.cod_div_par)"
                  @change="toggle(row.cod_div_par)"
                />
              </td>
              <td class="px-3 py-2 text-n-slate-12">
                {{ row.nome_contribuinte }}
              </td>
              <td class="px-3 py-2 text-n-slate-11">{{ row.celular }}</td>
              <td class="px-3 py-2 text-n-slate-11">{{ row.descricao_rep }}</td>
              <td class="px-3 py-2 text-n-slate-11">{{ row.ano_div }}</td>
              <td class="px-3 py-2 text-n-slate-11">{{ row.cod_div_par }}</td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>
